<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="importhistory-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Historia importów</h1>
      </div>
    </div>
    <v-card outlined class="rounded-lg" style="overflow: hidden;">
      <v-card-title>
        {{ $t('importHistory.title') }}
        <v-spacer></v-spacer>
        <button class="action-pill action-pill--secondary" @click="purgeDeleted" :disabled="purging">
          <v-icon left small>mdi-broom</v-icon>
          {{ purging ? 'Czyszczenie…' : 'Wyczyść cofnięte' }}
        </button>
      </v-card-title>
    <v-card-text class="py-2">
      <v-row>
        <v-col cols="12" md="3">
          <v-text-field
            :label="$t('importHistory.weekFrom')"
            v-model="filters.weekFrom"
            type="week"
            outlined
            dense
            hide-details
            @change="initalize"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            :label="$t('importHistory.weekTo')"
            v-model="filters.weekTo"
            type="week"
            outlined
            dense
            hide-details
            @change="initalize"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="search"
            :label="$t('common.search')"
            outlined
            dense
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card-text>
    <v-data-table
      :headers="headers"
      :items="imported"
      :options.sync="options"
      :server-items-length="totalItems"
      :loading="loading"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      class="elevation-0 import-history-table"
    >
      <template v-slot:item.fileName="{ item }">
        <v-chip
          v-if="(item.fileName || '').startsWith('WSTEPNE-')"
          x-small color="warning" outlined class="mr-1"
          title="Import wstępny z API. Wgraj CSV z Bolt portalu aby zaimportować finalny."
        >
          <v-icon x-small left>mdi-clock-alert-outline</v-icon>WSTĘPNE
        </v-chip>
        <span class="caption">{{ item.fileName }}</span>
      </template>
      <template v-slot:item.reImport="{ item }">
        <v-btn
          class="warning"
          v-if="!item.deleted && !item.isRent"
          @click="reImport(item.id)"
        >
          {{ $t('importHistory.reImportSkipped') }}
        </v-btn>
      </template>
      <template v-slot:item.skipped="{ item }">
        <v-btn
          class="info"
          small
          v-if="!item.isRent"
          @click="showSkipped(item.id)"
        >
          {{ $t('importHistory.showSkipped') }}
        </v-btn>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="d-flex" style="gap:6px; flex-wrap:wrap;">
          <v-btn v-if="!item.deleted && canUndoImport" small class="error" @click="undoImport(item.id)">
            <v-icon small left>mdi-undo</v-icon>{{ $t('importHistory.undoImport') }}
          </v-btn>
          <v-btn
            v-if="!item.deleted && canUndoImport"
            small
            color="error"
            outlined
            @click="undoImportGroup(item)"
          >
            <v-icon small left>mdi-undo-variant</v-icon>Cofnij grupę
          </v-btn>
          <v-btn
            v-if="!item.deleted && !item.isRent"
            small
            color="success"
            outlined
            :loading="rerunLoading[item.id]"
            @click="rerunRent(item)"
            title="Ponownie nalicz czynsz najmu — przydatne gdy dodano protokół najmu po imporcie"
          >
            <v-icon small left>mdi-cash-sync</v-icon>Nalicz najem
          </v-btn>
          <v-btn
            v-if="!item.deleted && !item.isRent"
            small
            color="error"
            outlined
            :loading="revertRentLoading[item.id]"
            @click="revertRent(item)"
            title="Cofnij naliczony najem za tydzień tego importu (usuwa tylko naliczenia, które nie weszły jeszcze do wypłat)"
          >
            <v-icon small left>mdi-cash-refund</v-icon>Cofnij najem
          </v-btn>
          <v-btn v-if="item.deleted" small disabled class="warning">
            {{ $t('importHistory.importCancelled') }}
          </v-btn>
        </div>
      </template>
      <template v-slot:item.download="{ item }">
        <v-btn class="secondary" @click="downloadFile(item.fileId)">
          {{ $t('importHistory.downloadFile') }}
        </v-btn>
      </template>
    </v-data-table>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { canUndoOperations as canUndoOps } from "@/services/permissions";
import * as dayjs from "dayjs";
import isoWeek from 'dayjs/plugin/isoWeek'
dayjs.extend(isoWeek)
export default {
  name: "ImportHistoryPage",
  data: () => ({
    search: "",
    filters: {
      // Domyślnie 4 tyg wstecz → bieżący — żeby user widział wszystkie świeże importy
      // niezależnie od tego z którego tygodnia są dane.
      // ISO week + isoWeekYear — bo <input type="week"> używa ISO 8601.
      // Calendar year ≠ ISO week year np. 2023-01-01 to ISO week 52 of 2022.
      weekFrom: dayjs().subtract(4, 'week').isoWeekYear() + '-W' + String(dayjs().subtract(4, 'week').isoWeek()).padStart(2, '0'),
      weekTo: dayjs().isoWeekYear() + '-W' + String(dayjs().isoWeek()).padStart(2, '0'),
    },
    options: {
      page: 1,
      itemsPerPage: 10,
    },
    totalItems: 0,
    loading: false,
    headers: [
      { text: "Data Importu", value: "date", sortable: true },
      { text: "Zaimportowano na", value: "importedFor", sortable: true },
      { text: "Nazwa", value: "name", sortable: true },
      { text: "Nazwa wzoru", value: "eqName", sortable: true },
      {
        text: "Nazwa Pliku",
        align: "start",
        value: "fileName",
        sortable: true,
      },
      { text: "", value: "actions", sortable: false },
      { text: "", value: "reImport", sortable: false },
      { text: "", value: "skipped", sortable: false },
      { text: "", value: "download", sortable: false },
    ],
    imported: [],
    role: localStorage.getItem("role"),
    rerunLoading: {},
    revertRentLoading: {},
    purging: false,
  }),
  computed: {
    canUndoImport() { return canUndoOps(); },
  },
  watch: {
    options: {
      async handler() {
        await this.initalize();
      },
      deep: true,
    },
    async search() {
      this.options.page = 1;
      await this.initalize();
    },
  },
  methods: {
    async purgeDeleted() {
      const confirm = await Swal.fire({
        title: 'Wyczyścić cofnięte importy?',
        text: 'Hard-delete: trwale usuwam importy oznaczone jako cofnięte (i powiązane ICD/Bonusy/pliki). Tej operacji nie da się cofnąć.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Usuń',
        cancelButtonText: 'Anuluj',
        confirmButtonColor: '#d33',
      });
      if (!confirm.isConfirmed) return;
      this.purging = true;
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'import/purge-deleted',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const r = res.data?.data || {};
        await Swal.fire({
          title: 'Wyczyszczone',
          html: `Importy: <b>${r.purgedImports || 0}</b><br>ICD: ${r.icdDeleted || 0}<br>Bonusy: ${r.bonusesDeleted || 0}<br>Pliki: ${r.filesDeleted || 0}`,
          icon: 'success',
        });
        await this.initalize();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się wyczyścić', 'error');
      }
      this.purging = false;
    },
    async rerunRent(item) {
      this.$set(this.rerunLoading, item.id, true);
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + `import/${item.id}/rerun-rent`,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const d = res.data?.data || {};
        if (d.addedCount > 0) {
          const totalZl = (d.addedTotalCents / 100).toFixed(2);
          await Swal.fire({
            title: 'Naliczono najem',
            html: `Dodano ${d.addedCount} naliczeń · łącznie ${totalZl} zł`,
            icon: 'success',
          });
        } else {
          await Swal.fire({
            title: 'Brak nowych naliczeń',
            text: 'Wszyscy kierowcy z najmem w tym tygodniu już mają naliczony czynsz, lub nie ma kierowców do naliczenia (sprawdź czy auto ma ustawioną cenę najmu i czy istnieje protokół najmu).',
            icon: 'info',
          });
        }
      } catch (e) {
        console.error(e);
        Swal.fire('Błąd', e?.response?.data?.message?.toString?.() || 'Nie udało się ponownie naliczyć najmu', 'error');
      } finally {
        this.$set(this.rerunLoading, item.id, false);
      }
    },
    async revertRent(item) {
      const confirm = await Swal.fire({
        title: 'Cofnąć najem za ten tydzień?',
        text: 'Usunę naliczenia czynszu najmu z tygodnia tego importu, które NIE weszły jeszcze do wypłat. Naliczenia zabrane do rozliczeń zostaną nietknięte (najpierw cofnij rozliczenie).',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Cofnij najem',
        cancelButtonText: 'Anuluj',
        confirmButtonColor: '#d33',
      });
      if (!confirm.isConfirmed) return;
      this.$set(this.revertRentLoading, item.id, true);
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + `import/${item.id}/revert-rent`,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const d = res.data?.data || {};
        const totalZl = ((d.removedTotalCents || 0) / 100).toFixed(2);
        const rows = (d.drivers || []).map((x) => `${x.name} — ${(x.amountCents / 100).toFixed(2)} zł`).join('<br>');
        await Swal.fire({
          title: d.removedCount ? 'Najem cofnięty' : 'Nic do cofnięcia',
          html: d.removedCount
            ? `Usunięto <b>${d.removedCount}</b> naliczeń na <b>${totalZl} zł</b>.<br><small>${rows}</small>` +
              (d.lockedCount ? `<br><br>⚠ ${d.lockedCount} naliczeń jest już w wypłatach — zostały nietknięte.` : '')
            : (d.lockedCount
              ? `Wszystkie naliczenia z tego tygodnia (${d.lockedCount}) są już w wypłatach — najpierw cofnij rozliczenie.`
              : 'W tym tygodniu nie ma naliczonego najmu.'),
          icon: d.removedCount ? 'success' : 'info',
        });
      } catch (e) {
        console.error(e);
        Swal.fire('Błąd', e?.response?.data?.message?.toString?.() || 'Nie udało się cofnąć najmu', 'error');
      } finally {
        this.$set(this.revertRentLoading, item.id, false);
      }
    },
    async toggleLoading(type) {
      const toggle = type;
      localStorage.setItem("loading", toggle);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
    async initalize() {
      this.loading = true;
      try {
        // Mapowanie nazw kolumn UI → pola backendu.
        // 'date' (kolumna „Data Importu") = createdAt
        // 'importedFor' (kolumna „Zaimportowano na") = date
        // 'name'/'fileName' = fileName
        // 'eqName' = equation.name (sortuje po nazwie wzoru)
        const uiCol = this.options.sortBy?.[0];
        const sortBy = uiCol === 'date' ? 'createdAt'
                     : uiCol === 'importedFor' ? 'date'
                     : uiCol === 'name' || uiCol === 'fileName' ? 'fileName'
                     : uiCol === 'eqName' ? 'eqName'
                     : 'createdAt';
        const sortOrder = this.options.sortDesc?.[0] ? 'desc' : 'asc';
        const parseWeek = (w) => {
          const [y, ws] = w.split('-');
          const wk = parseInt(ws.replace('W', ''));
          const yr = parseInt(y);
          const jan4 = dayjs(`${yr}-01-04`);
          const dow = jan4.day();
          const diff = (dow === 0 ? -6 : 1) - dow;
          return jan4.add(diff, 'day').add((wk - 1) * 7, 'day');
        };
        const start = parseWeek(this.filters.weekFrom);
        const end = parseWeek(this.filters.weekTo).add(6, 'day');
        const imported = await axios({
          method: "get",
          url:
            process.env.VUE_APP_ROOT_API +
            `import?take=${this.options.itemsPerPage}&skip=${this.options.page}&search=${encodeURIComponent(this.search)}&sortBy=${sortBy}&sortOrder=${sortOrder}&startDate=${start.format('YYYY-MM-DD')}&endDate=${end.format('YYYY-MM-DD')}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.imported = imported.data.data.result.map((imp) => {
          return {
            date: dayjs(imp.createdAt).format("YYYY-MM-DD HH:mm"),
            importedFor: dayjs(imp.date).format("YYYY-MM-DD HH:mm"),
            id: imp.id,
            name: imp.fileName,
            eqName: imp.equation?.name,
            fileName:
              imp.description ||
              (imp.file.length > 0 ? imp.file[0]?.orginalName : null),
            deleted: imp.deleted,
            isRent: imp.description,
            fileId: imp.file.length > 0 ? imp.file[0]?.id : null,
          };
        });
        this.totalItems = imported.data.data.count;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      this.loading = false;
    },
    async reImport(id) {
      await this.toggleLoading(true);
      try {
        Swal.fire({
          title: "Napewno?",
          text: "",
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Tak",
          cancelButtonText: "Anuluj",
        }).then(async (result) => {
          if (result.isConfirmed) {
            const res = await axios({
              method: "post",
              url: process.env.VUE_APP_ROOT_API + "import/re-import/" + id,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.initalize();
            const d = res.data?.data || {};
            const wrong = d.wrongCooperationType || [];
            const unknown = d.unknownIds || [];
            let html = `Przypisano ponownie: <b>${d.assigned || 0}</b> z ${d.totalSkipped || 0} pominiętych.`;
            if (unknown.length) {
              html += `<br><br>Nadal nieznane ID (<b>${unknown.length}</b>):<br><code style="font-size:11px">${unknown.join("<br>")}</code>`;
            }
            if (wrong.length) {
              const lines = wrong
                .map((w) => `${w.driverName} — typ <b>${w.driverCooperationType}</b> (ID: <code style="font-size:11px">${w.recordId}</code>)`)
                .join("<br>");
              html += `<br><br><b>${wrong.length}</b> ID pasuje do kierowców o INNYM typie współpracy niż wzór tego importu (<b>${d.equationCooperationType}</b>):<br>${lines}<br><br>Użyj "Importuj ponownie pominięte" na imporcie tego samego pliku ze wzorem o typie współpracy kierowcy.`;
            }
            Swal.fire({
              icon: d.assigned > 0 ? "success" : wrong.length || unknown.length ? "warning" : "info",
              title: "Reimport zakończony",
              html,
            });
          }
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async undoImport(id) {
      await this.toggleLoading(true);
      try {
        Swal.fire({
          title: "Cofnąć import?",
          text: "",
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Tak",
          cancelButtonText: "Anuluj",
        }).then(async (result) => {
          if (result.isConfirmed) {
            await axios({
              method: "post",
              url: process.env.VUE_APP_ROOT_API + "import/undo/" + id,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.initalize();
          }
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    /**
     * Cofa wszystkie importy z tego samego tygodnia (date) — przydatne gdy cron Mon 8:00
     * stworzył 4-5 importów per współpraca i chcemy je wszystkie cofnąć jednym kliknięciem.
     */
    async undoImportGroup(item) {
      const dateLabel = dayjs(item.importedFor).format('YYYY-MM-DD');
      const result = await Swal.fire({
        title: 'Cofnąć WSZYSTKIE importy z tego tygodnia?',
        html: `Tydzień rozliczeniowy: <b>${dateLabel}</b><br><br>` +
              `Cofnięte zostaną wszystkie importy z tym samym dniem rozliczenia ` +
              `(zwykle 4-5 importów — po jednym na każdą współpracę).<br>` +
              `<small class="grey--text">Wszystkie powiązane Settlements/Bonuses/ICD też będą oznaczone jako usunięte.</small>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d32f2f',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Tak, cofnij wszystkie',
        cancelButtonText: 'Anuluj',
      });
      if (!result.isConfirmed) return;
      await this.toggleLoading(true);
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'import/undo-group/' + item.id,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const d = res.data?.data || {};
        Swal.fire({
          icon: 'success',
          title: `Cofnięto ${d.undoneCount || 0} importów`,
          html: `<small>${(d.fileNames || []).join('<br>')}</small><br>` +
                `<br>ICD: ${d.icdCount || 0}, Bonusy: ${d.bonusesCount || 0}`,
        });
        await this.initalize();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message || e.message, 'error');
      }
      await this.toggleLoading(false);
    },
    showSkipped(importId) {
      location.href = '/skipped-import?id=' + importId;
    },
    async downloadFile(id) {
      if (!id) {
        Swal.fire("Błąd", "Brak pliku do pobrania", "error");
        return;
      }
      try {
        const file = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + id,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          responseType: "blob",
        });
        let blob = new Blob([file.data], { type: file.data.type }),
          url = window.URL.createObjectURL(blob);
        window.open(url);
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", "Nie udało się pobrać pliku", "error");
      }
    },
  },
};
</script>

<style scoped>
.importhistory-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.page-header { padding: 4px 4px 18px; }
.page-eyebrow { font-size: 12px; color: #6E7691; font-weight: 500; text-transform: capitalize; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }
.importhistory-page >>> .v-card { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; box-shadow: none !important; color: #fff !important; }
.importhistory-page >>> .v-card .v-card__title { color: #fff !important; padding: 16px 20px 8px !important; font-weight: 700 !important; font-size: 15px !important; }
.importhistory-page >>> .v-text-field--outlined fieldset { border-color: rgba(255,255,255,0.1) !important; }
.importhistory-page >>> .v-text-field--outlined .v-input__slot { background: #181C28 !important; border-radius: 12px !important; }
.importhistory-page >>> .v-label { color: #6E7691 !important; }
.importhistory-page >>> .v-text-field input { color: #fff !important; }
.importhistory-page >>> .v-data-table { background: transparent !important; color: #fff !important; }
.importhistory-page >>> .v-data-table thead tr th { background: #1F2434 !important; color: #6E7691 !important; font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.importhistory-page >>> .v-data-table tbody tr td { color: #fff !important; border-bottom: 1px solid rgba(255,255,255,0.04) !important; }
.importhistory-page >>> .v-data-footer { background: #1F2434 !important; color: #B0BEC5 !important; border-top: 1px solid rgba(255,255,255,0.06) !important; }
.importhistory-page >>> .v-btn:not(.v-btn--icon) { border-radius: 10px !important; text-transform: none !important; font-weight: 600 !important; }
.importhistory-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.importhistory-page >>> .v-divider { border-color: rgba(255,255,255,0.06) !important; }
.importhistory-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }
.importhistory-page >>> .v-dialog .v-card { background: #232838 !important; }
.importhistory-page >>> pre { background: #181C28 !important; color: #B0BEC5 !important; border: 1px solid rgba(255,255,255,0.06); }

/* Wyraźniejszy kontrast wierszy + odstępy żeby się nie zlewały */
.import-history-table ::v-deep tbody tr {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.import-history-table ::v-deep tbody tr:nth-child(even) {
  background-color: rgba(255, 255, 255, 0.025);
}
.import-history-table ::v-deep tbody tr:hover {
  background-color: rgba(63, 81, 181, 0.12) !important;
}
.import-history-table ::v-deep tbody td {
  padding: 14px 16px !important;
  font-size: 14px !important;
}
.import-history-table ::v-deep thead th {
  padding: 12px 16px !important;
  font-size: 12px !important;
  letter-spacing: 0.04em !important;
  text-transform: uppercase;
}
/* Przyciski w wierszach — niech mają trochę luzu */
.import-history-table ::v-deep tbody td .v-btn {
  margin: 2px 4px 2px 0;
}
</style>
